import React, { useState } from 'react';
import { ScrollView, View, TouchableOpacity, Linking, Image } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text, Card, Chip, Button, Banner } from '@ui';
import { useJourneys, useSuggestions, useAdvisor } from '@api/hooks';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '@navigation/types';

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
`;

const Section = styled.View`
  padding: ${({ theme }) => theme.spacing.base}px;
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
`;

const SectionHeader = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const ChipsContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}px;
  margin-top: ${({ theme }) => theme.spacing.md}px;
`;

const SuggestionCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const SuggestionHeader = styled.View`
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
`;

const IconWrapper = styled.View<{ priority: 'high' | 'medium' | 'low' }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background-color: ${({ theme, priority }) => {
    switch (priority) {
      case 'high':
        return theme.colors.errorLight;
      case 'medium':
        return theme.colors.warningLight;
      default:
        return theme.colors.infoLight;
    }
  }};
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.md}px;
`;

const BenefitsGrid = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md}px;
  margin-top: ${({ theme }) => theme.spacing.md}px;
`;

const BenefitCard = styled(TouchableOpacity)`
  width: 48%;
  padding: ${({ theme }) => theme.spacing.base}px;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md}px;
  border-width: 1px;
  border-color: ${({ theme }) => theme.colors.border};
  min-height: 100px;
  justify-content: center;
  align-items: center;
`;

const AdvisorCard = styled(Card)`
  flex-direction: row;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.md}px;
`;

const Avatar = styled(Image)`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background-color: ${({ theme }) => theme.colors.neutral200};
  margin-right: ${({ theme }) => theme.spacing.md}px;
`;

const HeroImage = styled(Image)`
  width: 100%;
  height: 180px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const AdvisorInfo = styled.View`
  flex: 1;
`;

const AdvisorActions = styled.View`
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.sm}px;
`;

const ActionButton = styled(TouchableOpacity)`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  align-items: center;
  justify-content: center;
`;

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const HomeScreen: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp>();
  const [showBanner, setShowBanner] = useState(true);

  const { data: journeys, isLoading: journeysLoading } = useJourneys();
  const { data: suggestions, isLoading: suggestionsLoading } = useSuggestions();
  const { data: advisor, isLoading: advisorLoading } = useAdvisor();

  const activeJourneys = journeys?.filter(j => j.isActive) || [];

  const handleJourneyPress = (journeyId: string) => {
    navigation.navigate('JourneyDetail', { journeyId });
  };

  const handleBenefitPress = (type: string, title: string) => {
    navigation.navigate('DeepLink', { type, title });
  };

  const handlePregnancyTipsPress = () => {
    navigation.navigate('PregnancyTips');
  };

  const handleChatbotPress = () => {
    navigation.navigate('Chatbot');
  };

  const handleAdvisorAction = (action: 'chat' | 'call' | 'whatsapp') => {
    if (action === 'chat') {
      navigation.navigate('Main', { screen: 'Profile' }); // Navigate to advisor messaging
    } else if (action === 'call' && advisor?.phone) {
      Linking.openURL(`tel:${advisor.phone}`);
    } else if (action === 'whatsapp' && advisor?.whatsapp) {
      Linking.openURL(`https://wa.me/${advisor.whatsapp.replace(/[^0-9]/g, '')}`);
    }
  };

  return (
    <Container edges={['top']}>
      {showBanner && (
        <Banner
          variant="info"
          message={t('compliance.banner')}
          dismissible
          onDismiss={() => setShowBanner(false)}
        />
      )}
      <Content showsVerticalScrollIndicator={false}>
        {/* Header with Hero Image */}
        <Section>
          <HeroImage
            source={{
              uri: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
            }}
            resizeMode="cover"
          />
          <Text variant="headline" color="textPrimary">
            {t('home.greeting')}! 👋
          </Text>
          <Text variant="body" color="textSecondary" style={{ marginTop: 4 }}>
            Welcome to your personalized health companion
          </Text>
        </Section>

        {/* My Moments */}
        <Section>
          <SectionHeader>
            <Text variant="title" color="textPrimary">
              {t('home.myMoments')}
            </Text>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => navigation.navigate('Main', { screen: 'Journeys' })}
            >
              {t('journey.viewAll')}
            </Button>
          </SectionHeader>
          <ChipsContainer>
            {activeJourneys.map(journey => (
              <Chip
                key={journey.id}
                variant="filled"
                icon={journey.icon}
                onPress={() => handleJourneyPress(journey.id)}
              >
                {journey.title}
              </Chip>
            ))}
          </ChipsContainer>
        </Section>

        {/* Smart Suggestions */}
        <Section>
          <Text variant="title" color="textPrimary" style={{ marginBottom: 12 }}>
            {t('home.smartSuggestions')}
          </Text>
          {suggestions?.map(suggestion => (
            <SuggestionCard key={suggestion.id} padding="md">
              <SuggestionHeader>
                <IconWrapper priority={suggestion.priority}>
                  <Icon
                    name={suggestion.icon}
                    size={24}
                    color={
                      suggestion.priority === 'high'
                        ? theme.colors.error
                        : suggestion.priority === 'medium'
                          ? theme.colors.warning
                          : theme.colors.info
                    }
                  />
                </IconWrapper>
                <View style={{ flex: 1 }}>
                  <Text variant="titleMedium" color="textPrimary">
                    {suggestion.title}
                  </Text>
                </View>
              </SuggestionHeader>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 12 }}>
                {suggestion.description}
              </Text>
              {suggestion.actionLabel && (
                <Button variant="tertiary" size="sm" iconRight="arrow-forward">
                  {suggestion.actionLabel}
                </Button>
              )}
            </SuggestionCard>
          ))}
        </Section>

        {/* Benefits & Programs */}
        <Section>
          <Text variant="title" color="textPrimary">
            {t('home.benefits')}
          </Text>
          <BenefitsGrid>
            <BenefitCard onPress={() => handleBenefitPress('benefits', 'Employee Benefits')}>
              <Icon name="work" size={32} color={theme.colors.brandPrimary} />
              <Text
                variant="bodyMedium"
                color="textPrimary"
                align="center"
                style={{ marginTop: 8 }}
              >
                Employee Benefits
              </Text>
            </BenefitCard>
            <BenefitCard onPress={() => handleBenefitPress('benefits', 'MPF')}>
              <Icon name="account-balance" size={32} color={theme.colors.brandPrimary} />
              <Text
                variant="bodyMedium"
                color="textPrimary"
                align="center"
                style={{ marginTop: 8 }}
              >
                MPF
              </Text>
            </BenefitCard>
            <BenefitCard onPress={() => handleBenefitPress('benefits', 'Life Insurance')}>
              <Icon name="favorite" size={32} color={theme.colors.brandPrimary} />
              <Text
                variant="bodyMedium"
                color="textPrimary"
                align="center"
                style={{ marginTop: 8 }}
              >
                Life Insurance
              </Text>
            </BenefitCard>
            <BenefitCard onPress={() => handleBenefitPress('wellness', 'Wellness')}>
              <Icon name="spa" size={32} color={theme.colors.brandPrimary} />
              <Text
                variant="bodyMedium"
                color="textPrimary"
                align="center"
                style={{ marginTop: 8 }}
              >
                Wellness
              </Text>
            </BenefitCard>
            <BenefitCard onPress={handlePregnancyTipsPress}>
              <Icon name="pregnant-woman" size={32} color={theme.colors.brandPrimary} />
              <Text
                variant="bodyMedium"
                color="textPrimary"
                align="center"
                style={{ marginTop: 8 }}
              >
                1st Trimester Tips
              </Text>
            </BenefitCard>
            <BenefitCard onPress={handleChatbotPress}>
              <Icon name="chat" size={32} color={theme.colors.brandPrimary} />
              <Text
                variant="bodyMedium"
                color="textPrimary"
                align="center"
                style={{ marginTop: 8 }}
              >
                Chatbot
              </Text>
            </BenefitCard>
          </BenefitsGrid>
        </Section>

        {/* My Advisor */}
        {advisor && (
          <Section>
            <Text variant="title" color="textPrimary" style={{ marginBottom: 12 }}>
              {t('home.myAdvisor')}
            </Text>
            <AdvisorCard padding="md">
              <Avatar
                source={{
                  uri: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80',
                }}
                resizeMode="cover"
              />
              <AdvisorInfo>
                <Text variant="titleMedium" color="textPrimary">
                  {advisor.name}
                </Text>
                <Text variant="bodySmall" color="textSecondary">
                  {advisor.title}
                </Text>
                <Chip variant="tonal" size="sm" style={{ marginTop: 4, alignSelf: 'flex-start' }}>
                  {advisor.slaResponse}
                </Chip>
              </AdvisorInfo>
              <AdvisorActions>
                <ActionButton onPress={() => handleAdvisorAction('whatsapp')}>
                  <Icon name="chat" size={20} color={theme.colors.textOnPrimary} />
                </ActionButton>
                <ActionButton onPress={() => handleAdvisorAction('call')}>
                  <Icon name="phone" size={20} color={theme.colors.textOnPrimary} />
                </ActionButton>
              </AdvisorActions>
            </AdvisorCard>
          </Section>
        )}

        <View style={{ height: 24 }} />
      </Content>
    </Container>
  );
};

export default HomeScreen;
